/** ===== Wishlist Drawer — Custom JS ===== */
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];

const CONFIG = {
    FOCUSABLE: 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
};

/* ---------- Helpers ---------- */
function el(tag, attrs = {}, text = '') {
    const e = document.createElement(tag);
    Object.assign(e, attrs);
    if (text) e.textContent = text;
    return e;
}

/** Poll until a selector appears in the DOM (SWishlist injects elements after DOMContentLoaded). */
function pollFor(selector, max = 100, ms = 300) {
    return new Promise((resolve, reject) => {
        let n = 0;
        const id = setInterval(() => {
            const found = $(selector);
            if (found) {
                clearInterval(id);
                resolve(found);
                return;
            }
            if (++n >= max) {
                clearInterval(id);
                reject(selector);
            }
        }, ms);
    });
}

const shopifyFetch = (url, opts = {}) =>
    fetch(url, opts).then(r => r.ok ? r.json() : Promise.reject(r));

/* ---------- UI Updates ---------- */
async function updateCartUI() {
    try {
        const { item_count } = await shopifyFetch('/cart.js');
        const icon = $('#cart-icon-bubble');
        if (!icon) return;

        let bubble = icon.querySelector('.cart-count-bubble');

        if(item_count === 0) {
            if(bubble) bubble.remove();
            return;
        }

        if (!bubble) {
            bubble = el('div', {className: 'cart-count-bubble'});
            icon.appendChild(bubble);
        }

        bubble.innerHTML =
            `<span aria-hidden="true"${item_count >= 100 ? ' class="hidden"' : ''}>${item_count}</span>` +
            `<span class="visually-hidden">${item_count} ${item_count === 1 ? 'item' : 'items'}</span>`;
    } catch (e) { console.error('Cart UI error:', e); }
}

/** Clear → delay → set ensures screen readers re-announce identical messages. */
function announce(msg) {
    let r = $('#wishlist-live-region');
    if (!r) {
        r = el('div', { id: 'wishlist-live-region', className: 'visually-hidden' });
        r.setAttribute('role', 'status');
        r.setAttribute('aria-live', 'polite');
        r.setAttribute('aria-atomic', 'true');
        document.body.appendChild(r);
    }
    r.textContent = '';
    setTimeout(() => { r.textContent = msg; }, 100);
}

/* ---------- Add-to-cart (shared) ---------- */
async function handleAdd(btn, label, payload) {
    btn.disabled = true;
    btn.textContent = 'Adding...';
    try {
        await shopifyFetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        btn.textContent = 'Added!';
        announce('Item added to cart');
        await updateCartUI();
    } catch (e) {
        btn.textContent = 'Error';
        console.error('Add-to-cart error:', e);
    } finally {
        setTimeout(() => { btn.disabled = false; btn.textContent = label; }, 2000);
    }
}

/* ---------- Wishlist product buttons ---------- */
function initWishlistActions() {
    const container = $('.st-wishlist-details_products');
    if (!container) return;

    // Per-product "Add to cart"
    $$('.st-atc-button', container).forEach(orig => {
        if (orig.previousElementSibling?.classList.contains('st-wishlist-add-to-cart')) return;
        const select = orig.closest('form')?.querySelector('select[name="id"]');
        const b = el('button', { type: 'button', className: 'st-wishlist-add-to-cart' }, 'Add to cart');
        b.addEventListener('click', () => {
            const id = select?.value;
            if (id) handleAdd(b, 'Add to cart', { id, quantity: 1 });
        });
        orig.style.display = 'none';
        orig.parentNode.insertBefore(b, orig);
    });
}

/* ---------- Focus trap (WCAG) ---------- */
let _trapCleanup = null;

function isDrawerOpen(drawer) {
    // SWishlist toggles `st-is-open` on the parent `.st-wishlist-details`
    const parent = drawer.closest('.st-wishlist-details');
    return parent ? parent.classList.contains('st-is-open') : false;
}

function setTrap(drawer, on, triggerEl) {
    if (!on) {
        _trapCleanup?.();
        _trapCleanup = null;
        triggerEl?.focus();
        return;
    }

    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('aria-label', 'Wishlist');

    const firstFocusable = drawer.querySelector('.st-close-button') || drawer.querySelector(CONFIG.FOCUSABLE);
    setTimeout(() => firstFocusable?.focus(), 100);

    const handleKey = (e) => {
        if (e.key === 'Escape') { drawer.querySelector('.st-close-button')?.click(); setTrap(drawer, false, triggerEl); return; }
        if (e.key !== 'Tab') return;

        const els = $$(CONFIG.FOCUSABLE, drawer).filter(f => f.offsetWidth > 0);
        if (!els.length) return;
        const first = els[0], last = els[els.length - 1];

        if (!drawer.contains(document.activeElement)) { e.preventDefault(); first.focus(); return; }
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };

    document.addEventListener('keydown', handleKey);
    _trapCleanup = () => {
        document.removeEventListener('keydown', handleKey);
        drawer.removeAttribute('aria-modal');
    };
}

/* ---------- Boot ---------- */
document.addEventListener('DOMContentLoaded', () => {
    // 1. Product buttons — wait for SWishlist product list
    pollFor('.st-wishlist-details_products')
        .then(productsContainer => {
            let debounce = null;
            const productsObserver = new MutationObserver(() => {
                productsObserver.disconnect();
                clearTimeout(debounce);
                debounce = setTimeout(() => {
                    initWishlistActions();
                    productsObserver.observe(productsContainer, { childList: true, subtree: true });
                }, 300);
            });
            initWishlistActions();
            setTimeout(initWishlistActions, 10000); // backup: re-run after 10s in case Storeify was slow
            productsObserver.observe(productsContainer, { childList: true, subtree: true });
        })
        .catch(() => console.warn('Wishlist products container not found.'));

    // 2. Focus trap — wait for SWishlist drawer panel
    pollFor('.st-wishlist-details_wrapper')
        .then(drawerPanel => {
            const triggerBtn = $('.st-wishlist-details-button--header-icon');
            const drawerRoot = drawerPanel.closest('.st-wishlist-details');
            if (!triggerBtn || !drawerRoot) return;

            // Activate only on explicit user click
            triggerBtn.addEventListener('click', () => {
                let n = 0;
                const checkOpen = setInterval(() => {
                    if (isDrawerOpen(drawerPanel)) { clearInterval(checkOpen); setTrap(drawerPanel, true, triggerBtn); }
                    if (++n > 20) clearInterval(checkOpen);
                }, 100);
            });

            // Deactivate when app closes the drawer (watch root for `st-is-open` toggle)
            new MutationObserver(() => {
                if (_trapCleanup && !isDrawerOpen(drawerPanel)) setTrap(drawerPanel, false, triggerBtn);
            }).observe(drawerRoot, { attributes: true, attributeFilter: ['class'] });
        })
        .catch(() => console.warn('Wishlist drawer not found for focus-trap.'));
});
